<script setup lang="ts">
import { cloneDeep } from 'lodash'
import { computed, VNode } from 'vue'

import { HandballMissedThrowAttribute } from '@collector/sportsapi-client-legacy'
import { useIncidentGroupComponents } from '@mobile/views/Relation/DetailsPanel/Modals/IncidentDetailsModal/composables/useIncidentGroupComponents'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'

import { DetailButtonsConfiguration } from '../../../composables/useSportOptions'
import { useThrowPositionDetailsModalForm } from '../components/ThrowPositionDetailsModal/composables'
import ThrowPositionDetailsModal from '../components/ThrowPositionDetailsModal/ThrowPositionDetailsModal.vue'
import { GroupName } from '../consts'
import {
  groups,
  pitchDistanceAttributeMap,
  pitchPositionAttributeMap,
} from './consts'

interface Props {
  linkedIncident: ProbableLinkedIncident
}

const props = defineProps<Props>()

// emits
const emit = defineEmits(['close'])

// data
const localLinkedIncident = cloneDeep(props.linkedIncident)

// composables
const incidentGroupComponentsComposable = useIncidentGroupComponents(
  localLinkedIncident,
  groups,
)
const {
  updateIncident: _updateIncident,
  removeAttribute,
  addAttribute,
  addGroupAttribute,
} = incidentGroupComponentsComposable
const { pitchDistance, pitchPosition } = useThrowPositionDetailsModalForm(
  localLinkedIncident,
  removeAttribute,
  addAttribute,
  pitchDistanceAttributeMap,
  pitchPositionAttributeMap,
  undefined,
  () => {
    const styleGroup = groups.get(
      GroupName.Style,
    ) as DetailButtonsConfiguration<HandballMissedThrowAttribute>

    addGroupAttribute(styleGroup, HandballMissedThrowAttribute.Air)
  },
)

// computed
const isThrowPositionFulfilled = computed(() => {
  return !!(pitchDistance.value && pitchPosition.value)
})

// methods
function updateIncident(): void {
  _updateIncident()

  emit('close')
}

function getGroupComponent(name: GroupName): VNode | null {
  return incidentGroupComponentsComposable.getGroupComponent(groups.get(name)!)
}
</script>

<template>
  <ThrowPositionDetailsModal
    v-model:pitchDistance="pitchDistance"
    v-model:pitchPosition="pitchPosition"
    :linkedIncident="localLinkedIncident"
    :isThrowPositionFulfilled
    pitchPositionName="Throw position"
    goalPositionDisabled
    @close="$emit('close')"
    @save="updateIncident"
  >
    <component :is="getGroupComponent(GroupName.Style)" />
    <component :is="getGroupComponent(GroupName.Type)" />
  </ThrowPositionDetailsModal>
</template>
